import type { EmotionalVoiceAvailability, ScenarioPersonaDto } from '@/api-gen/api.schemas';
import { EmotionalVoiceAvailability as EmotionalVoiceAvailabilityEnum, PersonaUsage } from '@/api-gen/api.schemas';

export type CapabilityId = 'intro' | 'agonal' | 'cpr' | 'emotion' | 'background';

/** Catalog order is the order capabilities are listed in, both in the section and in the add dialog. */
export const CAPABILITY_IDS: readonly CapabilityId[] = ['intro', 'agonal', 'cpr', 'emotion', 'background'] as const;

/** Capabilities only admins may add, edit or remove. */
export const ADMIN_ONLY_CAPABILITIES: ReadonlySet<CapabilityId> = new Set<CapabilityId>(['cpr']);

/** The backend's built-in counting tempo when the scenario has none set. */
export const DEFAULT_COUNTING_BPM = 110;

/** Mirrors the scenario schema's counting range (backend CountingMarkup.MinBpm/MaxBpm). */
export const MIN_COUNTING_BPM = 30;
export const MAX_COUNTING_BPM = 240;

/** Mirrors the backend's Scenario.BackgroundSoundVolume default; the range is 0–100. */
export const DEFAULT_BACKGROUND_SOUND_VOLUME = 15;

/** The form values a capability reads and writes. */
export interface CapabilityFormState {
  playScenarioContextOn?: boolean | null;
  scenarioContextIntro?: string | null;
  paceCountingEnabled?: boolean | null;
  defaultCountingBpm?: number | null;
  /** Emotional voice is on unless switched off; the backend applies it only where it is available. */
  emotionalVoiceEnabled?: boolean | null;
  /** Computed by the backend on the saved scenario; unknown (null) until the scenario is saved. */
  emotionalVoiceAvailability?: EmotionalVoiceAvailability | null;
  scenarioPersonas?: ScenarioPersonaDto[] | null;
  /** The looping background sound; null means none. */
  backgroundSoundId?: number | null;
  backgroundSoundVolume?: number | null;
}

/** Why emotional voice cannot apply to the scenario, as the backend reported it; null when it can. */
export type EmotionBlockReason = 'language' | 'voice';

export const getEmotionBlockReason = (form: CapabilityFormState): EmotionBlockReason | null => {
  switch (form.emotionalVoiceAvailability) {
    case EmotionalVoiceAvailabilityEnum.UnsupportedLanguage:
      return 'language';
    case EmotionalVoiceAvailabilityEnum.NoElevenLabsVoice:
      return 'voice';
    default:
      return null;
  }
};

/**
 * Whether the capability appears at all. Emotional voice depends on a backend verdict that only exists
 * for a saved scenario, so an unsaved scenario neither lists nor offers it.
 */
export const isCapabilityOffered = (id: CapabilityId, form: CapabilityFormState): boolean =>
  id !== 'emotion' || form.emotionalVoiceAvailability != null;

/**
 * Everything a settings dialog edits before it is saved to the form. Personas added or removed in the
 * dialog's picker change only `personas`, so Cancel leaves the scenario untouched.
 */
export interface CapabilityDraft {
  personaId: number | null;
  text: string;
  bpm: string;
  personas: ScenarioPersonaDto[];
  soundId: number | null;
  volume: number;
}

const hasUsage = (sp: ScenarioPersonaDto, usage: PersonaUsage) => sp.personaUsages?.includes(usage) ?? false;

export const findPersonaWithUsage = (personas: ScenarioPersonaDto[] | null | undefined, usage: PersonaUsage) =>
  (personas ?? []).find((sp) => hasUsage(sp, usage));

/** The persona that reads the intro: the Intro persona, else the default one, as the backend resolves it. */
export const getEffectiveIntroPersona = (personas: ScenarioPersonaDto[] | null | undefined) =>
  findPersonaWithUsage(personas, PersonaUsage.Intro) ?? findPersonaWithUsage(personas, PersonaUsage.Default);

/** The capabilities switched on in the form, in catalog order. */
export const getActiveCapabilities = (form: CapabilityFormState): CapabilityId[] =>
  CAPABILITY_IDS.filter((id) => {
    switch (id) {
      case 'intro':
        return !!form.playScenarioContextOn;
      case 'agonal':
        return !!findPersonaWithUsage(form.scenarioPersonas, PersonaUsage.AgonalBreathing);
      case 'cpr':
        return !!form.paceCountingEnabled;
      case 'emotion':
        // The first default-on capability: added unless switched off, and only where the backend says it applies.
        return (
          form.emotionalVoiceAvailability === EmotionalVoiceAvailabilityEnum.Available &&
          form.emotionalVoiceEnabled !== false
        );
      case 'background':
        return form.backgroundSoundId != null;
    }
  });

/** Starting values for a settings dialog: the capability's current settings, or sensible defaults when adding. */
export const getInitialDraft = (id: CapabilityId, form: CapabilityFormState): CapabilityDraft => {
  const empty: CapabilityDraft = {
    personaId: null,
    text: '',
    bpm: '',
    personas: form.scenarioPersonas ?? [],
    soundId: null,
    volume: DEFAULT_BACKGROUND_SOUND_VOLUME
  };
  switch (id) {
    case 'intro':
      return {
        ...empty,
        personaId: getEffectiveIntroPersona(form.scenarioPersonas)?.personaId ?? null,
        text: form.scenarioContextIntro ?? ''
      };
    case 'agonal':
      return {
        ...empty,
        personaId: findPersonaWithUsage(form.scenarioPersonas, PersonaUsage.AgonalBreathing)?.personaId ?? null
      };
    case 'cpr':
      return { ...empty, bpm: String(form.defaultCountingBpm ?? DEFAULT_COUNTING_BPM) };
    case 'emotion':
      return empty;
    case 'background':
      return {
        ...empty,
        soundId: form.backgroundSoundId ?? null,
        volume: form.backgroundSoundVolume ?? DEFAULT_BACKGROUND_SOUND_VOLUME
      };
  }
};

/**
 * Why a persona can't be the agonal-breathing patient: the breathing persona never speaks, so it
 * can't also be the default persona, or the intro persona while intro audio is on. A leftover Intro
 * usage with intro audio off doesn't block; choosing the persona clears it. Returns null when it can be chosen.
 */
export const getAgonalBlockReason = (sp: ScenarioPersonaDto, introActive: boolean): 'default' | 'intro' | null => {
  if (hasUsage(sp, PersonaUsage.Default)) {
    return 'default';
  }

  return introActive && hasUsage(sp, PersonaUsage.Intro) ? 'intro' : null;
};

/** The breathing patient can't read the intro either; choosing it here would silently end agonal breathing. */
export const getIntroBlockReason = (sp: ScenarioPersonaDto): 'agonal' | null =>
  hasUsage(sp, PersonaUsage.AgonalBreathing) ? 'agonal' : null;

export const parseBpm = (bpm: string): number | null => {
  const trimmed = bpm.trim();
  if (trimmed === '') {
    return null;
  }

  const value = Number(trimmed);
  return Number.isInteger(value) ? value : null;
};

export const isCapabilityDraftValid = (id: CapabilityId, draft: CapabilityDraft, introActive = false): boolean => {
  const persona = draft.personas.find((sp) => sp.personaId === draft.personaId);
  switch (id) {
    case 'intro':
      return !!persona && getIntroBlockReason(persona) === null && draft.text.trim() !== '';
    case 'agonal':
      return !!persona && getAgonalBlockReason(persona, introActive) === null;
    case 'cpr': {
      const bpm = parseBpm(draft.bpm);
      return bpm != null && bpm >= MIN_COUNTING_BPM && bpm <= MAX_COUNTING_BPM;
    }
    case 'emotion':
      return true;
    case 'background':
      return draft.soundId != null && draft.volume >= 0 && draft.volume <= 100;
  }
};
